import { useState, useEffect, useCallback } from 'react';
import { Download, X, AlertTriangle, Sparkles } from 'lucide-react';
import api, { apiUrl } from '../utils/apiClient';

const API = process.env.REACT_APP_BACKEND_URL;

// iter190 — APK distribution is paused while we move to Play Store /
// TestFlight; the public mobile-app surface is now /mobile (Coming
// Soon + waitlist). Until the native app ships, this banner has
// nothing actionable to offer, so we short-circuit early. Flip
// ENABLE_NATIVE_APP_UPDATE_BANNER back to true once the stores are live.
const ENABLE_NATIVE_APP_UPDATE_BANNER = false;

const AppUpdateBanner = () => {
  if (!ENABLE_NATIVE_APP_UPDATE_BANNER) return null;
  // The legacy implementation below is preserved verbatim so re-enabling
  // is a one-line change.
  return <LegacyAppUpdateBanner />;
};

const LegacyAppUpdateBanner = () => {
  const [updateInfo, setUpdateInfo] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const getCurrentVersionCode = useCallback(() => {
    // In Capacitor, we can get version from the app plugin
    // For web, check localStorage for last known version
    try {
      if (window.Capacitor?.isNativePlatform?.()) {
        // Will be set by Capacitor App plugin
        return parseInt(localStorage.getItem('app_version_code') || '0', 10);
      }
    } catch {}
    return 0; // Web users always see 0 = no native app
  }, []);

  const checkForUpdate = useCallback(async () => {
    try {
      const { data } = await api.get(apiUrl('/app/version'));

      const currentCode = getCurrentVersionCode();

      // Only show if: native app AND version is behind
      // OR if force_update is true for everyone
      if (data.version_code > currentCode && currentCode > 0) {
        setUpdateInfo(data);
      }

      // Also show for web users if there's a new APK and they haven't dismissed recently
      if (currentCode === 0 && !sessionStorage.getItem('update_dismissed')) {
        // Don't show to web users unless we detect they're on mobile browser
        const isMobileBrowser = /Android|iPhone|iPad/i.test(navigator.userAgent);
        if (isMobileBrowser && !window.Capacitor?.isNativePlatform?.()) {
          setUpdateInfo({ ...data, isWebPrompt: true });
        }
      }
    } catch (err) {
      console.error('Update check failed:', err);
    }
  }, [getCurrentVersionCode]);

  useEffect(() => {
    // Check for update after a 3-second delay (don't block initial load)
    const timer = setTimeout(checkForUpdate, 3000);
    return () => clearTimeout(timer);
  }, [checkForUpdate]);

  const handleDownload = () => {
    if (!updateInfo?.download_url) return;
    setDownloading(true);
    const url = updateInfo.download_url.startsWith('http')
      ? updateInfo.download_url
      : `${API}${updateInfo.download_url}`;
    window.open(url, '_blank');
    setTimeout(() => setDownloading(false), 2000);
  };

  const handleDismiss = () => {
    if (updateInfo?.force_update) return; // Can't dismiss forced updates
    setDismissed(true);
    sessionStorage.setItem('update_dismissed', 'true');
  };

  if (!updateInfo || dismissed) return null;

  const isForced = updateInfo.force_update && !updateInfo.isWebPrompt;
  const isWebPrompt = updateInfo.isWebPrompt;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-[90] p-4 ${
        isForced ? 'bg-amber-600' : 'bg-gradient-to-r from-blue-600 to-indigo-600'
      } text-white shadow-lg shadow-black/30 animate-slide-up`}
      data-testid="app-update-banner"
    >
      <div className="max-w-xl mx-auto flex items-center gap-3">
        {isForced ? (
          <AlertTriangle className="w-6 h-6 flex-shrink-0" />
        ) : (
          <Sparkles className="w-6 h-6 flex-shrink-0" />
        )}

        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm leading-tight">
            {isWebPrompt
              ? 'Get the iStudyPro App'
              : isForced
                ? 'Critical Update Required'
                : `Update Available (v${updateInfo.version_name})`}
          </p>
          <p className="text-xs text-white/80 mt-0.5 truncate">
            {isWebPrompt
              ? 'Download our Android app for the best experience'
              : updateInfo.release_notes || 'New features and improvements'}
          </p>
        </div>

        <button aria-label="Download"
          onClick={handleDownload}
          disabled={downloading}
          className="flex items-center gap-1.5 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl text-sm font-semibold transition-colors flex-shrink-0"
          data-testid="app-update-download-btn"
        >
          <Download className={`w-4 h-4 ${downloading ? 'animate-bounce' : ''}`} />
          {downloading ? 'Opening...' : isWebPrompt ? 'Install' : 'Update'}
        </button>

        {!isForced && (
          <button aria-label="Close"
            onClick={handleDismiss}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0"
            data-testid="app-update-dismiss-btn"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AppUpdateBanner;
